//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';

import { getEchoThemeComponents } from '@utils/themeUtils';

const FormControlDisabledOverrides = `
  color: ${theme.colors.gray4};
  background-color: ${theme.colors.gray0};
  border-color: ${theme.colors.gray2};
`;

const FocusProps = `
  border-color: ${theme.colors.aliases.focus};
  background-color: ${theme.colors.white};
  outline: none;
  box-shadow:
    inset 0 0 0 ${theme.borderWidths[1]} ${theme.colors.aliases.focus},
    inset 0 0 0 ${theme.borderWidths[2]} ${theme.colors.cyan2};
`;

/**
 * Separate declarations of base styles so that they can be easily
 * extended in the primary mixins declaration.
 */
const FormControlBase = `
  border-radius: ${theme.borderRadii[1]};
  box-sizing: border-box;
  height: ${theme.spacings[5]};
  padding: ${theme.spacings[2]};
  color: ${theme.colors.aliases.bodyPrimary};
  border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};

  &::placeholder {
    color: ${theme.colors.gray4};
    opacity: 1;
  }

  &[disabled] {
    ${FormControlDisabledOverrides}
  }

  &:not([disabled]) {
    &:hover {
      box-shadow: ${theme.boxShadows[1]};

      &:read-only {
        box-shadow: none;
      }
    }

    &:focus,
    &.cu-focus {
      ${FocusProps}

      &:read-only {
        box-shadow: none;
      }
    }
  }
`;

/*
 * This set of mixins allows you to add sets of Unison styles to React
 * styled-components without potential css specificity conflicts. These
 * should take the place of attaching a Unison style class to a component
 * via the .attrs() helper.
 *
 * Usage:
 *
 * const FlexSheet = styled.div`
 *   ${ Mixins.Sheet }
 *   display: flex;
 * `;
 *
 * const SuccessTitle = styled.div`
 *   ${ Mixins.Text.Title }
 *   color: ${ theme.colors.aliases.success };
 * `;
 *
 */

const createTextStyle = (textStyle: {
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
  color: string;
  textTransform?: string;
  letterSpacing?: string;
}) => `
  font-size: ${textStyle.fontSize};
  font-weight: ${textStyle.fontWeight};
  line-height: ${textStyle.lineHeight};
  color: ${textStyle.color};
  ${textStyle.textTransform ? `text-transform: ${textStyle.textTransform};` : ''}
  ${textStyle.letterSpacing ? `letter-spacing: ${textStyle.letterSpacing};` : ''}
`;

const createLinkStyle = (linkStyle: {
  default: {
    fontSize: string;
    fontWeight: string;
    lineHeight: string;
    color: string;
  };
  hover: {
    fontSize: string;
    fontWeight: string;
    lineHeight: string;
    color: string;
  };
  disabled?: { color: string };
}) => `
  && {
    font-size: ${linkStyle.default.fontSize};
    font-weight: ${linkStyle.default.fontWeight};
    line-height: ${linkStyle.default.lineHeight};
    color: ${linkStyle.default.color};
  }

  &&:not([disabled]):not(.cu-disabled):hover{
    font-size: ${linkStyle.hover.fontSize};
    font-weight: ${linkStyle.hover.fontWeight};
    line-height: ${linkStyle.hover.lineHeight};
    color: ${linkStyle.hover.color};
  }

  // TODO: Add focus styles for links

  ${
    linkStyle.disabled
      ? `
    &&[disabled],
    &&.cu-disabled {
      color: ${linkStyle.disabled.color};
      text-decoration: none;
      cursor: not-allowed;
    }
  `
      : ''
  }
`;

const textStyles = getEchoThemeComponents(theme).text;

export const Mixins = {
  Text: {
    Display: createTextStyle(textStyles.display),
    Title: createTextStyle(textStyles.title),
    Subtitle: createTextStyle(textStyles.subtitle),
    Section: createTextStyle(textStyles.section),
    SectionBold: createTextStyle(textStyles.sectionBold),
    Subsection: createTextStyle(textStyles.subsection),
    Metatag: createTextStyle(textStyles.metatag),
    Badge: createTextStyle(textStyles.badge),
    Body: createTextStyle(textStyles.body),
    BodyBold: createTextStyle(textStyles.bodyBold),
    BodySecondary: createTextStyle(textStyles.bodySecondary),
    BodyDisabled: createTextStyle(textStyles.bodyDisabled),
    BodyError: createTextStyle(textStyles.bodyError),
    Caption: createTextStyle(textStyles.caption),
    CaptionSecondary: createTextStyle(textStyles.captionSecondary),
    CaptionError: createTextStyle(textStyles.captionError),
    Label: createTextStyle(textStyles.label),
    LinkPrimary: createLinkStyle(textStyles.linkPrimary),
    LinkBodyPrimary: createLinkStyle(textStyles.linkBodyPrimary),
    LinkCaptionPrimary: createLinkStyle(textStyles.linkCaptionPrimary),
    LinkSecondary: createLinkStyle(textStyles.linkSecondary),
    LinkBodySecondary: `
      ${createLinkStyle(textStyles.linkBodySecondary)}
      border-radius: ${textStyles.linkBodySecondary.default.borderRadius};
      box-shadow: inset 0 0 0 1.5px ${textStyles.linkBodySecondary.default.color};
      
      &:not([disabled]):hover,
      &:not(.cu-disabled):hover {
        background-color: ${textStyles.linkBodySecondary.hover.background} !important;
        color: ${textStyles.linkBodySecondary.hover.color} !important;
        box-shadow: inset 0 0 0 1.5px ${textStyles.linkBodySecondary.hover.background} !important;
      }
    `,
    TableHeaderCell: createTextStyle(textStyles.tableHeaderCell),
    TableDataPrimary: createTextStyle(textStyles.tableDataPrimary),
    TableDataSecondary: createTextStyle(textStyles.tableDataSecondary),
    // UiCopy styles use fontSize as lineHeight from body style due to Supernova proceesing only px values
    // this sets the font-size and line-height to the same value
    UiCopy: `
      ${createTextStyle(textStyles.body)}
      line-height: ${textStyles.body.fontSize};
    `,
    UiCopyBold: `
      ${createTextStyle(textStyles.bodyBold)}
      line-height: ${textStyles.body.fontSize};
    `,
    UiCopySecondary: `
      ${createTextStyle(textStyles.bodySecondary)}
      line-height: ${textStyles.body.fontSize};
    `,
    UiCopyDisabled: `
      ${createTextStyle(textStyles.bodyDisabled)}
      line-height: ${textStyles.body.fontSize};
    `,
    UiCopyError: `
      ${createTextStyle(textStyles.bodyError)}
      line-height: ${textStyles.body.fontSize};
    `,
  },
  Sheet: {
    Low: `
      display: flex;
      background-color: ${theme.colors.white};
      box-shadow: ${theme.boxShadows[1]};
    `,
    Medium: `
      display: flex;
      background-color: ${theme.colors.white};
      box-shadow: ${theme.boxShadows[2]};
    `,
    High: `
      display: flex;
      background-color: ${theme.colors.white};
      box-shadow: ${theme.boxShadows[3]};
    `,
  },
  FormControl: {
    Primary: FormControlBase,
    Error:
      FormControlBase +
      `
      border-color: ${theme.colors.aliases.error};
      background-color: ${theme.colors.red0};
      outline: 0;
    `,
    Disabled: FormControlBase + FormControlDisabledOverrides,
  },
  Border: {
    MediaError: `
      border: ${theme.borderWidths[2]} solid ${theme.colors.aliases.error}
    `,
  },
  HiddenVideoVolumeControls: `
    video::-webkit-media-controls-mute-button,
    video::-webkit-media-controls-volume-control-container,
    video::-webkit-media-controls-volume-slider-container,
    video::-webkit-media-controls-volume-slider {
      display: none !important;
    }
  `,
  /**
   * VisuallyHidden mixin provides styles for components to be completely invisible to sighted readers,
   * but properly read by screen readers on all browsers.
   *
   * For more information on this approach, see this article: https://snook.ca/archives/html_and_css/hiding-content-for-accessibility
   */
  VisuallyHidden: `
    position: absolute !important;
    height: 1px;
    width: 1px;
    padding: 0 !important;
    border: 0 !important;
    overflow: hidden;
    white-space: nowrap !important;
    clip: rect(1px, 1px, 1px, 1px);
  `,
  Focus: FocusProps,
} as const;

export type TextMixin = (typeof Mixins)['Text'][keyof (typeof Mixins)['Text']];
